import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { LoanStage } from "@toorak/tc-common-fe-sdk";
import { LoanStatusEnum } from "@toorak/tc-common-util-sdk";
import { useDispatch } from "react-redux";
import { useStyles } from "../create-loan/common-style/CreateLoan.style";
import { setActiveTabAndProperty } from "../create-loan/create-loan.action";
import { FeatureFlag, useFeatureFlag } from "../feature-flags";
import { createTcOfacTransport } from "./createTcOfacTransport";
import {
  ofacHeaderChipSpec,
  pickOfacLoanStatus,
  shouldShowOfacHeaderChip
} from "./ofacHeaderChip";
import {
  subscribeOfacHeaderRefresh,
  subscribeOfacHeaderStatus
} from "./ofacHeaderRefresh";
import { preloadOfacScreening } from "./preloadOfacScreening";
import type { LoanStatus } from "./initiatePurchaseOfac";

export interface OfacLoanHeaderChipProps {
  loanId: string;
  loanState: LoanStatusEnum;
  loanStage: LoanStage;
}

/**
 * Loan Detail header OFAC status chip (T9). Reads POST /bff/ofac/status once on
 * page load (no Bridger). Accepts roll-up status from the tab without re-POSTing.
 * Preloads the OFAC MF remote while the chip is visible. Behind TA-11941.
 */
export function OfacLoanHeaderChip({
  loanId,
  loanState,
  loanStage
}: Readonly<OfacLoanHeaderChipProps>) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const enabled = useFeatureFlag(FeatureFlag.OFAC_SCREENING);
  const visible =
    enabled && Boolean(loanId) && shouldShowOfacHeaderChip({ loanState, loanStage });
  const transport = useMemo(() => createTcOfacTransport(), []);
  const [status, setStatus] = useState<LoanStatus | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => subscribeOfacHeaderRefresh(() => setRefreshToken((n) => n + 1)), []);
  useEffect(() => subscribeOfacHeaderStatus((next) => setStatus(next)), []);

  useEffect(() => {
    if (visible) preloadOfacScreening();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    transport
      .getLoanStatus([loanId])
      .then((res) => {
        if (!cancelled) setStatus(pickOfacLoanStatus(res, loanId));
      })
      .catch(() => {
        if (!cancelled) setStatus("OFAC NOT SCREENED");
      });
    return () => {
      cancelled = true;
    };
  }, [visible, loanId, transport, refreshToken]);

  // Hide via `visible` — do not clear status in an effect (avoids sync setState-in-effect).
  if (!visible || status == null) return null;

  const chip = ofacHeaderChipSpec(status);
  const openOfacTab = () => {
    dispatch(setActiveTabAndProperty("ofacScreening"));
  };

  return (
    <Typography
      data-testid="ofac-loan-header-chip"
      variant="h5"
      className={classes.itemTypography}
      style={{ ...chip.style, cursor: "pointer" }}
      role="button"
      tabIndex={0}
      onClick={openOfacTab}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openOfacTab();
        }
      }}
      aria-label={`${chip.label}. Open Sanctions Screening tab.`}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 12 }} htmlColor="#ffffff" />
        <span>{chip.label}</span>
        <OpenInNewIcon sx={{ fontSize: 12 }} htmlColor="#ffffff" />
      </span>
    </Typography>
  );
}
